import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, Clock, ShieldCheck } from "lucide-react";

import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { getCertificateTrail } from "@/components/onboarding/OnboardingTrail";
import type { SituationChoice } from "@/pages/onboarding/OnboardingSituationStep";

export interface OnboardingAwaitingCertificateStepProps {
  /** ID Digital confirmed the Abitab payment — the middle stage. */
  paymentReceived?: boolean;
  /** The certificate arrived and Fiscalo stored it. Terminal; shows Continuar. */
  certificateReady?: boolean;
  /** Advances the flow. Available at every stage — see the actions row. */
  onContinue?: () => void;
  situationChoice: SituationChoice | null;
  /** A CFE import is queued after the cert — drives the 4-row trail. */
  hasCfeImportNext: boolean;
}

/**
 * The wait between "Ya fui a Abitab" and the certificate landing in Fiscalo.
 *
 * Three stages on one screen — waiting for Abitab to confirm the payment,
 * payment confirmed and ID Digital issuing, certificate stored — because from
 * the user's side they are the same moment: nothing is being asked of them.
 * The screen exists to say so without making the silence read as a fault.
 *
 * Replaces the AwaitingAbitabCard rendering, which was the last Fiscalo-cert
 * state still on the legacy chrome (PageHeader + a white Card + the old
 * footer). It sits directly after OnboardingAbitabPresentStep, so acknowledging
 * the Abitab visit used to drop the user out of the editorial shell mid-flow.
 */
export function OnboardingAwaitingCertificateStep({
  paymentReceived = false,
  certificateReady = false,
  onContinue,
  situationChoice,
  hasCfeImportNext,
}: OnboardingAwaitingCertificateStepProps): JSX.Element {
  const { t } = useTranslation();

  // certificateReady wins: it is the later stage, and both flags are set once
  // the certificate lands.
  const stage = certificateReady
    ? "ready"
    : paymentReceived
      ? "payment"
      : "awaiting";

  // This screen only exists on the Fiscalo-managed path, so the trail is that
  // path's. It is the CERTIFICATE trail, not the journey trail: the eyebrow
  // says "Certificado digital", and a pane still listing Situación and Acceso
  // DGI was counting a different process from the one it named.
  const trailSteps = getCertificateTrail("fiscalo");
  // Abitab (row 3) until they confirm the payment; the certificate row the
  // moment they do, because from then on the remaining work is ID Digital
  // issuing it and us installing it.
  const stepTagStep = stage === "awaiting" ? 3 : 4;
  // The total IS the trail. A formula that has to agree with the trail will
  // eventually stop agreeing with it.
  const stepTagTotal = trailSteps.length;

  const tagLabel =
    stage === "ready"
      ? t("onboarding.awaitingCertificate.tagReady", "Certificado listo")
      : stage === "payment"
        ? t("onboarding.awaitingCertificate.tagPayment", "Pago confirmado")
        : t("onboarding.awaitingCertificate.tag", "Esperando a Abitab");

  const headline: ReactNode =
    stage === "ready" ? (
      <>
        {t("onboarding.awaitingCertificate.h1LeadReady", "Tu certificado")}{" "}
        <span className="ital">
          {t(
            "onboarding.awaitingCertificate.h1AccentReady",
            "ya está en Fiscalo.",
          )}
        </span>
      </>
    ) : stage === "payment" ? (
      <>
        {t("onboarding.awaitingCertificate.h1LeadPayment", "Pago confirmado,")}{" "}
        <span className="ital">
          {t(
            "onboarding.awaitingCertificate.h1AccentPayment",
            "falta que lo emitan.",
          )}
        </span>
      </>
    ) : (
      <>
        {t("onboarding.awaitingCertificate.h1Lead", "Ya está en manos")}{" "}
        <span className="ital">
          {t("onboarding.awaitingCertificate.h1Accent", "de Abitab.")}
        </span>
      </>
    );

  const lead =
    stage === "ready"
      ? t(
          "onboarding.awaitingCertificate.leadReady",
          "Lo guardamos en tu cuenta y lo usamos para firmar tus facturas electrónicas. No tenés que instalar nada.",
        )
      : stage === "payment"
        ? t(
            "onboarding.awaitingCertificate.leadPayment",
            "Recibimos la confirmación del pago en Abitab. Te avisamos por correo apenas ID Digital emita tu certificado.",
          )
        : t(
            "onboarding.awaitingCertificate.lead",
            "Cuando Abitab confirme el pago, ID Digital emite el certificado y nosotros lo subimos a Fiscalo por vos. Te avisamos por correo.",
          );

  const StatusIcon =
    stage === "awaiting" ? Clock : stage === "payment" ? CheckCircle2 : ShieldCheck;

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={stepTagStep}
            total={stepTagTotal}
            label={tagLabel}
            flow={t("onboarding.awaitingCertificate.flow", "Certificado digital")}
          />
          <h1 className="fx-onb-h1">{headline}</h1>
          <p className="fx-onb-lead">{lead}</p>

          {/* Reuses the step6* strings the legacy card used, so the copy fixed
              earlier today carries over rather than being rewritten here. */}
          <div
            style={{
              marginTop: 24,
              padding: "16px 18px",
              borderRadius: "var(--fx-r-md)",
              background: "var(--fx-s-raised-hover)",
              border: "1px solid var(--fx-s-hairline)",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <StatusIcon
              aria-hidden
              style={{
                width: 18,
                height: 18,
                flexShrink: 0,
                marginTop: 1,
                color:
                  stage === "awaiting"
                    ? "var(--fx-ink-500)"
                    : "var(--fx-accent-500)",
              }}
            />
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--fx-s-text)",
                }}
              >
                {stage === "ready"
                  ? t("onboarding.step6CertificateReady.title")
                  : stage === "payment"
                    ? t("onboarding.step6PaymentReceived.title")
                    : t("onboarding.step6AwaitingAbitab.title")}
              </p>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "var(--fx-s-text)",
                }}
              >
                {stage === "ready"
                  ? t("onboarding.step6CertificateReady.description")
                  : stage === "payment"
                    ? t("onboarding.step6PaymentReceived.description")
                    : t("onboarding.step6AwaitingAbitab.description")}
              </p>
            </div>
          </div>

          {/* No Volver on purpose. Back lands on "pasá por Abitab" — a screen
              telling someone to go and do a thing they have already done.
              Forward is the only real move, and only once the cert exists. */}
          {/* Forward at EVERY stage, not only once the certificate lands.
              Waiting on ID Digital can take until the next day, and none of
              the remaining onboarding needs the certificate — so parking the
              tenant here was asking them to sit out a wait they have no part
              in. Still their choice: we offer it, we do not skip the screen
              for them. */}
          {onContinue ? (
            <div className="fx-onb-actions">
              <div className="side" />
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={onContinue}
                >
                  {stage === "ready"
                    ? t("onboarding.next", "Continuar")
                    : t(
                        "onboarding.awaitingCertificate.continueWhileWaiting",
                        "Seguir configurando",
                      )}
                  <ArrowRight aria-hidden style={{ width: 16, height: 16 }} />
                </button>
              </div>
            </div>
          ) : null}
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={
            stage === "ready"
              ? t("onboarding.awaitingCertificate.brandEyebrowReady", "Listo")
              : t(
                  "onboarding.awaitingCertificate.brandEyebrow",
                  "Fiscalo se encarga",
                )
          }
          quote={
            stage === "ready" ? (
              <>
                {t(
                  "onboarding.awaitingCertificate.brandQuoteLeadReady",
                  "Ya podés emitir.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.awaitingCertificate.brandQuoteAccentReady",
                    "Firmamos cada factura por vos.",
                  )}
                </span>
              </>
            ) : (
              <>
                {t(
                  "onboarding.awaitingCertificate.brandQuoteLead",
                  "Desde acá no hace falta que hagas nada.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.awaitingCertificate.brandQuoteAccent",
                    "Te escribimos cuando esté.",
                  )}
                </span>
              </>
            )
          }
          currentStep={stepTagStep}
          trailSteps={trailSteps}
          valueBlock={
            stage === "ready"
              ? {
                  head: t(
                    "onboarding.awaitingCertificate.brandValueHeadReady",
                    "Qué acabás de ganar",
                  ),
                  items: [
                    t(
                      "onboarding.awaitingCertificate.brandValueReady1",
                      "El certificado queda guardado y cifrado en Fiscalo",
                    ),
                    t(
                      "onboarding.awaitingCertificate.brandValueReady2",
                      "Firmamos tus CFE automáticamente, sin instalar nada",
                    ),
                    t(
                      "onboarding.awaitingCertificate.brandValueReady3",
                      "Te avisamos antes de que venza para renovarlo",
                    ),
                  ],
                }
              : {
                  head: t(
                    "onboarding.awaitingCertificate.brandValueHead",
                    "Mientras tanto",
                  ),
                  items: [
                    t(
                      "onboarding.awaitingCertificate.brandValue1",
                      "Abitab confirma el pago a ID Digital",
                    ),
                    t(
                      "onboarding.awaitingCertificate.brandValue2",
                      "ID Digital emite el certificado",
                    ),
                    t(
                      "onboarding.awaitingCertificate.brandValue3",
                      "Lo subimos a Fiscalo por vos y te avisamos por correo",
                    ),
                    t(
                      "onboarding.awaitingCertificate.brandValue4",
                      "Podés cerrar esta pestaña — no perdés el avance",
                    ),
                  ],
                }
          }
        />
      }
    />
  );
}
